import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import { get_api_url } from '../../../../utils/urls';
import { api_with_method } from '../../../../redux/api_funcs';

const BASE_URL = '/v2/cms_user/recording_overrides';

const initialForm = {
  session_from_date: '',
  session_to_date: '',
  expires_date: '',
  reason: ''
};

const fmt_date = (val) => {
  if (!val || val === '0001-01-01T00:00:00Z') return '—';
  return val.split('T')[0];
};

const fmt_expires = (val) => {
  if (!val || val === '0001-01-01T00:00:00Z') return 'Permanent';
  return `${val.replace('T', ' ').split('.')[0].replace('Z', '')} UTC`;
};

// Session From → start of day (00:00:00) in tz → UTC
// Session To / Expires → end of day (23:59:59) in tz → UTC
const to_utc_str = (date_str, end_of_day, iana_timezone) => {
  if (!date_str) return '';
  const time = end_of_day ? '23:59:59' : '00:00:00';
  return moment
    .tz(`${date_str} ${time}`, 'YYYY-MM-DD HH:mm:ss', iana_timezone)
    .utc()
    .format('YYYY-MM-DD HH:mm:ss');
};

const from_utc_date = (utc_str, iana_timezone) => {
  if (!utc_str || utc_str === '0001-01-01T00:00:00Z') return '';
  return moment.utc(utc_str).tz(iana_timezone).format('YYYY-MM-DD');
};

export default function RecordingOverridesModal({
  student_uuid,
  iana_timezone = 'UTC',
  set_notification_variable
}) {
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const notify = (type, message) => set_notification_variable(true, type, message);

  const err_msg = (e) => {
    try {
      return e.response.data.message || e.response.data.reason;
    } catch (_) {
      return 'Something went wrong. Please contact the dev team.';
    }
  };

  const load = () => {
    setLoading(true);
    const url = get_api_url(`${BASE_URL}?student_uuid=${student_uuid}`, false, true);
    return api_with_method('get', url, null, true)
      .then((res) => {
        setOverrides(res.data.data || []);
        setLoading(false);
      })
      .catch((e) => {
        notify('error', err_msg(e));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (student_uuid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student_uuid]);

  const open_create = () => {
    setSelectedId(null);
    setForm(initialForm);
  };

  const select = (row) => {
    setSelectedId(row.id);
    setForm({
      session_from_date:
        fmt_date(row.session_from_date) === '—' ? '' : fmt_date(row.session_from_date),
      session_to_date: fmt_date(row.session_to_date) === '—' ? '' : fmt_date(row.session_to_date),
      expires_date: from_utc_date(row.override_expires_at, iana_timezone),
      reason: row.reason || ''
    });
  };

  const build_payload = () => ({
    session_from_date: form.session_from_date
      ? to_utc_str(form.session_from_date, false, iana_timezone).split(' ')[0]
      : '',
    session_to_date: form.session_to_date
      ? to_utc_str(form.session_to_date, true, iana_timezone).split(' ')[0]
      : '',
    override_expires_at: form.expires_date
      ? to_utc_str(form.expires_date, true, iana_timezone)
      : '',
    reason: form.reason
  });

  const validate = () => {
    if (!form.session_from_date) {
      notify('error', 'Session From Date is required.');
      return false;
    }
    if (!form.reason) {
      notify('error', 'Reason is required.');
      return false;
    }
    if (form.session_to_date && form.session_to_date < form.session_from_date) {
      notify('error', 'Session To Date must be on or after Session From Date.');
      return false;
    }
    if (form.expires_date && form.expires_date < form.session_from_date) {
      notify('error', 'Override Expires At must be on or after Session From Date.');
      return false;
    }
    return true;
  };

  const submit_create = () => {
    if (!validate()) return;
    setSubmitting(true);
    const url = get_api_url(BASE_URL, false, true);
    api_with_method('post', url, { ...build_payload(), student_uuid }, true)
      .then(() => load())
      .then(() => {
        notify('success', 'Override grant created.');
        setForm(initialForm);
        setSubmitting(false);
      })
      .catch((e) => {
        notify('error', err_msg(e));
        setSubmitting(false);
      });
  };

  const submit_edit = () => {
    if (!validate()) return;
    setSubmitting(true);
    const url = get_api_url(`${BASE_URL}/${selectedId}`, false, true);
    api_with_method('patch', url, build_payload(), true)
      .then(() => load())
      .then(() => {
        notify('success', 'Override grant updated.');
        setSelectedId(null);
        setForm(initialForm);
        setSubmitting(false);
      })
      .catch((e) => {
        notify('error', err_msg(e));
        setSubmitting(false);
      });
  };

  const delete_override = (id) => {
    if (!window.confirm('Soft-delete this override grant?')) return;
    const url = get_api_url(`${BASE_URL}/${id}`, false, true);
    api_with_method('delete', url, null, true)
      .then(() => load())
      .then(() => {
        notify('success', 'Override grant deleted.');
        if (selectedId === id) {
          setSelectedId(null);
          setForm(initialForm);
        }
      })
      .catch((e) => notify('error', err_msg(e)));
  };

  const input_style = {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid var(--input-border)',
    borderRadius: '4px',
    background: 'var(--input-bg)',
    color: 'var(--input-text)',
    width: '100%',
    boxSizing: 'border-box'
  };

  const label_style = {
    fontSize: '12px',
    color: 'var(--text-muted)',
    display: 'block',
    marginBottom: '4px'
  };

  return (
    <div style={{ minWidth: '720px', overflow: 'visible' }}>
      {loading ? (
        <p style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Loading...</p>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            marginBottom: '24px'
          }}
        >
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
              {['ID', 'Session From', 'Session To', 'Expires At (UTC)', 'Reason', ''].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '6px 10px',
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {overrides.map((row) => (
              <tr
                key={row.id}
                style={{
                  borderBottom: '1px solid var(--border-default)',
                  background:
                    row.id === selectedId
                      ? 'color-mix(in srgb, var(--brand-primary) 10%, transparent)'
                      : 'transparent'
                }}
              >
                <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{row.id}</td>
                <td style={{ padding: '8px 10px' }}>{fmt_date(row.session_from_date)}</td>
                <td style={{ padding: '8px 10px' }}>{fmt_date(row.session_to_date)}</td>
                <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                  {fmt_expires(row.override_expires_at)}
                </td>
                <td style={{ padding: '8px 10px' }}>{row.reason || '—'}</td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => select(row)}
                      style={{
                        padding: '3px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: '1px solid var(--border-default)',
                        borderRadius: '4px',
                        background: 'transparent',
                        color: 'var(--text-primary)'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => delete_override(row.id)}
                      style={{
                        padding: '3px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: '1px solid var(--state-error)',
                        borderRadius: '4px',
                        background: 'transparent',
                        color: 'var(--state-error)'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {overrides.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{ padding: '12px 10px', color: 'var(--text-muted)', fontStyle: 'italic' }}
                >
                  No active override grants for this student.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: '16px' }}>
        <p style={{ fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>
          {selectedId ? `Edit Grant #${selectedId}` : 'New Override Grant'}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '12px'
          }}
        >
          <div>
            <label style={label_style}>Session From Date *</label>
            <CustomDatePicker
              value={form.session_from_date}
              handleChange={(val) => setForm({ ...form, session_from_date: val })}
              popper_placement="bottom-start"
            />
            {form.session_from_date && (
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                → UTC: {to_utc_str(form.session_from_date, false, iana_timezone)}
              </p>
            )}
          </div>
          <div>
            <label style={label_style}>Session To Date</label>
            <CustomDatePicker
              value={form.session_to_date}
              handleChange={(val) => setForm({ ...form, session_to_date: val })}
              popper_placement="bottom-start"
            />
            {form.session_to_date && (
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                → UTC: {to_utc_str(form.session_to_date, true, iana_timezone)}
              </p>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={label_style}>
            Override Expires At — <strong>{iana_timezone}</strong>
          </label>
          <CustomDatePicker
            value={form.expires_date}
            handleChange={(val) => setForm({ ...form, expires_date: val })}
            popper_placement="bottom-start"
          />
          {form.expires_date && (
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              → UTC: {to_utc_str(form.expires_date, true, iana_timezone)}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={label_style}>Reason / Ticket ref *</label>
          <input
            type="text"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            style={input_style}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {selectedId && (
            <button
              onClick={open_create}
              disabled={submitting}
              style={{
                padding: '6px 16px',
                fontSize: '13px',
                cursor: 'pointer',
                border: '1px solid var(--border-default)',
                borderRadius: '4px',
                background: 'transparent',
                color: 'var(--text-primary)'
              }}
            >
              Cancel
            </button>
          )}
          <button
            onClick={selectedId ? submit_edit : submit_create}
            disabled={submitting}
            style={{
              padding: '6px 16px',
              fontSize: '13px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              border: 'none',
              borderRadius: '4px',
              background: 'var(--brand-primary)',
              color: '#fff',
              opacity: submitting ? 0.6 : 1
            }}
          >
            {submitting ? 'Saving...' : selectedId ? 'Save Changes' : 'Create Grant'}
          </button>
        </div>
      </div>
    </div>
  );
}
